<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import SettingIntroBanner from 'dashboard/components/widgets/SettingIntroBanner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const selectedInboxId = ref('');
const showValidation = ref(false);
const isSubmitting = ref(false);
const isUploadingMedia = ref(false);
const mediaFileInput = ref(null);
const carouselMediaFileInputs = ref([]);

const createButton = (type = 'URL') => ({
  type,
  text: '',
  url: '',
  phoneNumber: '',
  example: '',
});

const createCarouselCard = () => ({
  mediaSampleUrl: '',
  mediaSampleHandle: '',
  mediaSampleFile: null,
  mediaSampleFileName: '',
  body: '',
  buttons: [createButton()],
});

const template = reactive({
  format: 'STANDARD',
  name: '',
  category: 'MARKETING',
  language: 'ar',
  headerFormat: 'NONE',
  carouselHeaderFormat: 'IMAGE',
  headerText: '',
  headerSample: '',
  mediaSampleUrl: '',
  mediaSampleHandle: '',
  mediaSampleFile: null,
  mediaSampleFileName: '',
  body: '',
  bodySamples: {},
  footer: '',
  buttons: [],
  carouselCards: [createCarouselCard(), createCarouselCard()],
  hasCustomValidityPeriod: false,
  validityPeriod: '12h',
});

const whatsappInboxes = useMapGetter('inboxes/getWhatsAppInboxes');

const comboBoxClass =
  '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6';

const variablePattern = /\{\{(\d+)\}\}/g;
const templateNamePattern = /^[a-z0-9_]+$/;

function extractVariables(value) {
  return Array.from(value.matchAll(variablePattern))
    .map(match => Number(match[1]))
    .filter(
      (variable, index, variables) => variables.indexOf(variable) === index
    )
    .sort((first, second) => first - second);
}

function startsOrEndsWithVariable(value) {
  const trimmedValue = value.trim();
  return /^\{\{\d+\}\}/.test(trimmedValue) || /\{\{\d+\}\}$/.test(trimmedValue);
}

function hasSequentialVariables(variables) {
  return variables.every((variable, index) => variable === index + 1);
}

function variableLabel(variable) {
  return `{{${variable}}}`;
}

function variableLabelWithColon(variable) {
  return `${variableLabel(variable)}:`;
}

function addBodyVariable() {
  if (template.format !== 'STANDARD') return;

  const variables = extractVariables(template.body);
  const nextVariable = variables.length ? Math.max(...variables) + 1 : 1;
  const separator = template.body && !template.body.endsWith(' ') ? ' ' : '';
  template.body = `${template.body}${separator}${variableLabel(nextVariable)}`;
}

function applySamples(value, samples) {
  return value.replace(variablePattern, (_, variable) => {
    return samples[Number(variable)] || variableLabel(variable);
  });
}

function validityPeriodToSeconds(value) {
  if (value === '30d-default') return -1;

  const unit = value.slice(-1);
  const amount = Number(value.slice(0, -1));

  if (unit === 's') return amount;
  if (unit === 'm') return amount * 60;
  if (unit === 'h') return amount * 60 * 60;
  if (unit === 'd') return amount * 24 * 60 * 60;
  return amount;
}

function hasInterleavedButtonTypes(buttons) {
  const completedTypes = new Set();
  let currentType = '';

  return buttons.some(button => {
    if (button.type === currentType) return false;
    if (completedTypes.has(button.type)) return true;
    if (currentType) completedTypes.add(currentType);
    currentType = button.type;
    return false;
  });
}

const inboxOptions = computed(() =>
  (whatsappInboxes.value || []).map(inbox => ({
    value: inbox.id,
    label: inbox.name,
  }))
);

const selectedInboxName = computed(() => {
  const selectedInbox = (whatsappInboxes.value || []).find(
    inbox => inbox.id === Number(selectedInboxId.value)
  );

  return selectedInbox?.name || t('CAMPAIGN.TEMPLATES.BUILDER.PREVIEW.INBOX');
});

const formatOptions = computed(() => [
  {
    value: 'STANDARD',
    label: t('CAMPAIGN.TEMPLATES.BUILDER.FORMATS.STANDARD'),
    description: t('CAMPAIGN.TEMPLATES.BUILDER.FORMATS.STANDARD_DESCRIPTION'),
  },
  {
    value: 'CAROUSEL',
    label: t('CAMPAIGN.TEMPLATES.BUILDER.FORMATS.CAROUSEL'),
    description: t('CAMPAIGN.TEMPLATES.BUILDER.FORMATS.CAROUSEL_DESCRIPTION'),
  },
]);

const baseCategoryOptions = computed(() => [
  {
    value: 'MARKETING',
    label: t('CAMPAIGN.TEMPLATES.BUILDER.CATEGORIES.MARKETING'),
  },
  {
    value: 'UTILITY',
    label: t('CAMPAIGN.TEMPLATES.BUILDER.CATEGORIES.UTILITY'),
  },
  {
    value: 'AUTHENTICATION',
    label: t('CAMPAIGN.TEMPLATES.BUILDER.CATEGORIES.AUTHENTICATION'),
  },
]);

const categoryOptions = computed(() => {
  if (template.format === 'CAROUSEL') return [baseCategoryOptions.value[0]];
  return baseCategoryOptions.value;
});

const languageOptions = [
  { value: 'ar', label: 'العربية' },
  { value: 'en_US', label: 'English' },
];

const headerFormatOptions = computed(() => [
  {
    value: 'NONE',
    label: t('CAMPAIGN.TEMPLATES.BUILDER.HEADER_FORMATS.NONE'),
  },
  {
    value: 'TEXT',
    label: t('CAMPAIGN.TEMPLATES.BUILDER.HEADER_FORMATS.TEXT'),
  },
  {
    value: 'IMAGE',
    label: t('CAMPAIGN.TEMPLATES.BUILDER.HEADER_FORMATS.IMAGE'),
  },
  {
    value: 'VIDEO',
    label: t('CAMPAIGN.TEMPLATES.BUILDER.HEADER_FORMATS.VIDEO'),
  },
  {
    value: 'DOCUMENT',
    label: t('CAMPAIGN.TEMPLATES.BUILDER.HEADER_FORMATS.DOCUMENT'),
  },
]);

const buttonTypeOptions = computed(() => [
  {
    value: 'QUICK_REPLY',
    label: t('CAMPAIGN.TEMPLATES.BUILDER.BUTTON_TYPES.QUICK_REPLY'),
  },
  {
    value: 'URL',
    label: t('CAMPAIGN.TEMPLATES.BUILDER.BUTTON_TYPES.URL'),
  },
  {
    value: 'PHONE_NUMBER',
    label: t('CAMPAIGN.TEMPLATES.BUILDER.BUTTON_TYPES.PHONE_NUMBER'),
  },
  {
    value: 'COPY_CODE',
    label: t('CAMPAIGN.TEMPLATES.BUILDER.BUTTON_TYPES.COPY_CODE'),
  },
]);

const carouselButtonTypeOptions = computed(() =>
  buttonTypeOptions.value.filter(option => option.value !== 'COPY_CODE')
);

const carouselHeaderFormatOptions = computed(() =>
  headerFormatOptions.value.filter(option =>
    ['IMAGE', 'VIDEO'].includes(option.value)
  )
);

const validityPeriodOptions = computed(() => {
  if (template.category === 'AUTHENTICATION') {
    return [
      { value: '30s', label: '30s' },
      { value: '1m', label: '1m' },
      { value: '5m', label: '5m' },
      { value: '10m', label: '10m' },
      { value: '15m', label: '15m' },
      {
        value: '30d-default',
        label: t('CAMPAIGN.TEMPLATES.BUILDER.VALIDITY.DEFAULT_30_DAYS'),
      },
    ];
  }

  if (template.category === 'UTILITY') {
    return [
      { value: '30s', label: '30s' },
      { value: '1m', label: '1m' },
      { value: '5m', label: '5m' },
      { value: '10m', label: '10m' },
      { value: '30m', label: '30m' },
      { value: '1h', label: '1h' },
      { value: '6h', label: '6h' },
      { value: '12h', label: '12h' },
      {
        value: '30d-default',
        label: t('CAMPAIGN.TEMPLATES.BUILDER.VALIDITY.DEFAULT_30_DAYS'),
      },
    ];
  }

  return [
    { value: '12h', label: '12h' },
    { value: '24h', label: '24h' },
    { value: '48h', label: '48h' },
    { value: '72h', label: '72h' },
    { value: '96h', label: '96h' },
    { value: '168h', label: '168h' },
    { value: '30d', label: '30d' },
  ];
});

const hasHeaderMedia = computed(() =>
  ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(template.headerFormat)
);

function mediaAcceptFor(format) {
  if (format === 'IMAGE') return 'image/jpeg,image/png';
  if (format === 'VIDEO') return 'video/mp4,video/3gpp';
  if (format === 'DOCUMENT') return 'application/pdf';
  return '';
}

const mediaAccept = computed(() => mediaAcceptFor(template.headerFormat));
const carouselMediaAccept = computed(() =>
  mediaAcceptFor(template.carouselHeaderFormat)
);

const headerFormatLabel = computed(
  () =>
    headerFormatOptions.value.find(
      option => option.value === template.headerFormat
    )?.label || ''
);

const carouselHeaderFormatLabel = computed(
  () =>
    headerFormatOptions.value.find(
      option => option.value === template.carouselHeaderFormat
    )?.label || ''
);

const canAddButton = computed(() => template.buttons.length < 10);
const canAddCarouselCard = computed(() => template.carouselCards.length < 10);

const bodyVariables = computed(() => extractVariables(template.body));
const headerVariables = computed(() =>
  template.headerFormat === 'TEXT' ? extractVariables(template.headerText) : []
);

const allVariables = computed(() => [
  ...new Set([...headerVariables.value, ...bodyVariables.value]),
]);

const bodyWithSamples = computed(() =>
  applySamples(template.body, template.bodySamples)
);

const previewBodyPlaceholder = computed(() => {
  if (template.format === 'CAROUSEL') {
    return t('CAMPAIGN.TEMPLATES.BUILDER.PREVIEW.MESSAGE_BUBBLE_PLACEHOLDER');
  }

  return t('CAMPAIGN.TEMPLATES.BUILDER.PREVIEW.BODY_PLACEHOLDER');
});

const headerWithSample = computed(() => {
  if (template.headerFormat !== 'TEXT') return '';
  return applySamples(template.headerText, {
    1: template.headerSample,
  });
});

const visibleButtons = computed(() =>
  template.buttons.filter(button => button.text.trim() || button.example.trim())
);

function validateButtons(nextErrors) {
  const buttons = template.buttons.filter(
    button => button.text.trim() || button.example.trim()
  );
  const quickReplies = buttons.filter(button => button.type === 'QUICK_REPLY');
  const urlButtons = buttons.filter(button => button.type === 'URL');
  const phoneButtons = buttons.filter(button => button.type === 'PHONE_NUMBER');
  const copyCodeButtons = buttons.filter(button => button.type === 'COPY_CODE');

  if (buttons.length > 10) {
    nextErrors.buttons = t(
      'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.BUTTON_TOTAL_LIMIT'
    );
  }

  if (quickReplies.length > 10) {
    nextErrors.buttons = t(
      'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.QUICK_REPLY_LIMIT'
    );
  }

  if (urlButtons.length > 2) {
    nextErrors.buttons = t('CAMPAIGN.TEMPLATES.BUILDER.ERRORS.URL_LIMIT');
  }

  if (phoneButtons.length > 1) {
    nextErrors.buttons = t('CAMPAIGN.TEMPLATES.BUILDER.ERRORS.PHONE_LIMIT');
  }

  if (copyCodeButtons.length > 1) {
    nextErrors.buttons = t('CAMPAIGN.TEMPLATES.BUILDER.ERRORS.COPY_CODE_LIMIT');
  }

  if (hasInterleavedButtonTypes(buttons)) {
    nextErrors.buttons = t('CAMPAIGN.TEMPLATES.BUILDER.ERRORS.BUTTON_GROUPING');
  }

  template.buttons.forEach((button, index) => {
    if (!button.text.trim() && button.type !== 'COPY_CODE') return;

    const fieldErrors = {};
    if (button.type !== 'COPY_CODE' && !button.text.trim()) {
      fieldErrors.text = t('CAMPAIGN.TEMPLATES.BUILDER.ERRORS.BUTTON_TEXT');
    }

    if (button.type === 'URL' && !button.url.trim()) {
      fieldErrors.url = t('CAMPAIGN.TEMPLATES.BUILDER.ERRORS.BUTTON_URL');
    }

    if (button.type === 'PHONE_NUMBER' && !button.phoneNumber.trim()) {
      fieldErrors.phoneNumber = t(
        'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.BUTTON_PHONE'
      );
    }

    if (button.type === 'COPY_CODE' && !button.example.trim()) {
      fieldErrors.example = t(
        'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.BUTTON_COPY_CODE'
      );
    }

    if (Object.keys(fieldErrors).length) {
      nextErrors.buttonFields[index] = fieldErrors;
    }
  });
}

function validateButtonFields(button, fieldErrors) {
  if (button.type !== 'COPY_CODE' && !button.text.trim()) {
    fieldErrors.text = t('CAMPAIGN.TEMPLATES.BUILDER.ERRORS.BUTTON_TEXT');
  }

  if (button.type === 'URL' && !button.url.trim()) {
    fieldErrors.url = t('CAMPAIGN.TEMPLATES.BUILDER.ERRORS.BUTTON_URL');
  }

  if (button.type === 'PHONE_NUMBER' && !button.phoneNumber.trim()) {
    fieldErrors.phoneNumber = t(
      'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.BUTTON_PHONE'
    );
  }
}

function carouselButtonSignature(card) {
  return card.buttons
    .filter(button => button.text.trim())
    .map(button => button.type)
    .join('|');
}

function validateCarousel(nextErrors) {
  if (template.category !== 'MARKETING') {
    nextErrors.category = t(
      'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.CAROUSEL_MARKETING_ONLY'
    );
  }

  if (template.carouselCards.length < 2) {
    nextErrors.carousel = t(
      'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.CAROUSEL_CARD_MIN'
    );
  }

  if (template.carouselCards.length > 10) {
    nextErrors.carousel = t(
      'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.CAROUSEL_CARD_MAX'
    );
  }

  const firstSignature = carouselButtonSignature(template.carouselCards[0]);

  template.carouselCards.forEach((card, cardIndex) => {
    const cardErrors = { buttonFields: {} };
    const cardButtons = card.buttons.filter(button => button.text.trim());

    if (!card.mediaSampleFile) {
      cardErrors.mediaSampleFile = t(
        'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.MEDIA_SAMPLE_REQUIRED'
      );
    }

    if (!card.body.trim()) {
      cardErrors.body = t(
        'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.CAROUSEL_CARD_BODY_REQUIRED'
      );
    }

    if (extractVariables(card.body).length) {
      cardErrors.body = t(
        'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.CAROUSEL_CARD_VARIABLES'
      );
    }

    if (!cardButtons.length) {
      cardErrors.buttons = t(
        'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.CAROUSEL_BUTTON_REQUIRED'
      );
    }

    if (cardButtons.length > 2) {
      cardErrors.buttons = t(
        'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.CAROUSEL_BUTTON_LIMIT'
      );
    }

    if (firstSignature && carouselButtonSignature(card) !== firstSignature) {
      cardErrors.buttons = t(
        'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.CAROUSEL_BUTTON_STRUCTURE'
      );
    }

    card.buttons.forEach((button, buttonIndex) => {
      if (!button.text.trim()) return;

      const fieldErrors = {};
      validateButtonFields(button, fieldErrors);
      if (Object.keys(fieldErrors).length) {
        cardErrors.buttonFields[buttonIndex] = fieldErrors;
      }
    });

    if (
      cardErrors.mediaSampleFile ||
      cardErrors.body ||
      cardErrors.buttons ||
      Object.keys(cardErrors.buttonFields).length
    ) {
      nextErrors.carouselCards[cardIndex] = cardErrors;
    }
  });
}

const errors = computed(() => {
  const nextErrors = {
    inbox: '',
    name: '',
    language: '',
    category: '',
    carousel: '',
    header: '',
    headerText: '',
    headerSample: '',
    mediaSampleFile: '',
    body: '',
    footer: '',
    buttons: '',
    buttonFields: {},
    carouselCards: {},
    bodySamples: {},
  };

  if (!selectedInboxId.value) {
    nextErrors.inbox = t('CAMPAIGN.TEMPLATES.BUILDER.ERRORS.INBOX_REQUIRED');
  }

  if (!template.name.trim()) {
    nextErrors.name = t('CAMPAIGN.TEMPLATES.BUILDER.ERRORS.NAME_REQUIRED');
  } else if (!templateNamePattern.test(template.name)) {
    nextErrors.name = t('CAMPAIGN.TEMPLATES.BUILDER.ERRORS.NAME_FORMAT');
  }

  if (!template.language) {
    nextErrors.language = t(
      'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.LANGUAGE_REQUIRED'
    );
  }

  if (!template.category) {
    nextErrors.category = t(
      'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.CATEGORY_REQUIRED'
    );
  }

  if (template.format === 'STANDARD' && template.headerFormat === 'TEXT') {
    if (!template.headerText.trim()) {
      nextErrors.headerText = t(
        'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.HEADER_TEXT_REQUIRED'
      );
    } else if (template.headerText.length > 60) {
      nextErrors.headerText = t(
        'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.HEADER_TEXT_LENGTH'
      );
    }

    if (
      headerVariables.value.length > 1 ||
      headerVariables.value.some(v => v !== 1)
    ) {
      nextErrors.headerText = t(
        'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.HEADER_VARIABLE_LIMIT'
      );
    }

    if (headerVariables.value.length && !template.headerSample.trim()) {
      nextErrors.headerSample = t(
        'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.HEADER_SAMPLE_REQUIRED'
      );
    }
  } else if (
    template.format === 'STANDARD' &&
    hasHeaderMedia.value &&
    !template.mediaSampleFile
  ) {
    nextErrors.mediaSampleFile = t(
      'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.MEDIA_SAMPLE_REQUIRED'
    );
  }

  if (!template.body.trim()) {
    nextErrors.body = t('CAMPAIGN.TEMPLATES.BUILDER.ERRORS.BODY_REQUIRED');
  } else if (template.format === 'CAROUSEL' && bodyVariables.value.length) {
    nextErrors.body = t(
      'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.MESSAGE_BUBBLE_VARIABLES'
    );
  } else if (startsOrEndsWithVariable(template.body)) {
    nextErrors.body = t(
      'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.BODY_VARIABLE_BOUNDARY'
    );
  } else if (!hasSequentialVariables(bodyVariables.value)) {
    nextErrors.body = t(
      'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.BODY_VARIABLE_SEQUENCE'
    );
  }

  if (template.format === 'STANDARD') {
    bodyVariables.value.forEach(variable => {
      if (!String(template.bodySamples[variable] || '').trim()) {
        nextErrors.bodySamples[variable] = t(
          'CAMPAIGN.TEMPLATES.BUILDER.ERRORS.SAMPLE_REQUIRED',
          { variable }
        );
      }
    });
  }

  if (template.format === 'STANDARD' && template.footer.length > 60) {
    nextErrors.footer = t('CAMPAIGN.TEMPLATES.BUILDER.ERRORS.FOOTER_LENGTH');
  } else if (
    template.format === 'STANDARD' &&
    extractVariables(template.footer).length
  ) {
    nextErrors.footer = t('CAMPAIGN.TEMPLATES.BUILDER.ERRORS.FOOTER_VARIABLES');
  }

  if (template.format === 'CAROUSEL') {
    validateCarousel(nextErrors);
  } else {
    validateButtons(nextErrors);
  }

  return nextErrors;
});

const hasErrors = computed(() => {
  const topLevelErrors = Object.entries(errors.value).some(([key, value]) => {
    if (['buttonFields', 'bodySamples', 'carouselCards'].includes(key)) {
      return false;
    }
    return Boolean(value);
  });

  return (
    topLevelErrors ||
    Object.keys(errors.value.buttonFields).length > 0 ||
    Object.keys(errors.value.bodySamples).length > 0 ||
    Object.keys(errors.value.carouselCards).length > 0
  );
});

function buildButtonPayload(button) {
  if (button.type === 'URL') {
    return { type: 'URL', text: button.text, url: button.url };
  }
  if (button.type === 'PHONE_NUMBER') {
    return {
      type: 'PHONE_NUMBER',
      text: button.text,
      phone_number: button.phoneNumber,
    };
  }
  if (button.type === 'COPY_CODE') {
    return { type: 'COPY_CODE', example: button.example };
  }
  return { type: 'QUICK_REPLY', text: button.text };
}

const buildStandardPayload = mediaHandle => {
  const components = [];

  if (template.headerFormat !== 'NONE') {
    const headerComponent = {
      type: 'HEADER',
      format: template.headerFormat,
    };

    if (template.headerFormat === 'TEXT') {
      headerComponent.text = template.headerText;
      if (headerVariables.value.length) {
        headerComponent.example = {
          header_text: [template.headerSample],
        };
      }
    } else if (hasHeaderMedia.value) {
      headerComponent.example = {
        header_handle: [mediaHandle],
      };
    }

    components.push(headerComponent);
  }

  const bodyComponent = {
    type: 'BODY',
    text: template.body,
  };

  if (bodyVariables.value.length) {
    bodyComponent.example = {
      body_text: [
        bodyVariables.value.map(variable => template.bodySamples[variable]),
      ],
    };
  }

  components.push(bodyComponent);

  if (template.footer.trim()) {
    components.push({
      type: 'FOOTER',
      text: template.footer,
    });
  }

  const buttons = template.buttons
    .filter(button => button.text.trim() || button.example.trim())
    .map(buildButtonPayload);

  if (buttons.length) {
    components.push({
      type: 'BUTTONS',
      buttons,
    });
  }

  const nextPayload = {
    name: template.name,
    language: template.language,
    category: template.category,
    components,
  };

  if (template.hasCustomValidityPeriod) {
    nextPayload.message_send_ttl_seconds = validityPeriodToSeconds(
      template.validityPeriod
    );
  }

  return nextPayload;
};

const buildCarouselPayload = mediaHandles => {
  const bodyComponent = {
    type: 'BODY',
    text: template.body,
  };

  if (bodyVariables.value.length) {
    bodyComponent.example = {
      body_text: [
        bodyVariables.value.map(variable => template.bodySamples[variable]),
      ],
    };
  }

  const carouselComponent = {
    type: 'CAROUSEL',
    cards: template.carouselCards.map((card, index) => {
      const components = [
        {
          type: 'HEADER',
          format: template.carouselHeaderFormat,
          example: {
            header_handle: [mediaHandles[index]],
          },
        },
        {
          type: 'BODY',
          text: card.body,
        },
      ];

      const buttons = card.buttons
        .filter(button => button.text.trim())
        .map(buildButtonPayload);

      if (buttons.length) {
        components.push({
          type: 'BUTTONS',
          buttons,
        });
      }

      return { components };
    }),
  };

  const nextPayload = {
    name: template.name,
    language: template.language,
    category: 'MARKETING',
    components: [bodyComponent, carouselComponent],
  };

  if (template.hasCustomValidityPeriod) {
    nextPayload.message_send_ttl_seconds = validityPeriodToSeconds(
      template.validityPeriod
    );
  }

  return nextPayload;
};

const addButton = () => {
  if (!canAddButton.value) return;

  template.buttons.push(createButton());
};

const removeButton = index => {
  template.buttons.splice(index, 1);
};

const addCarouselCard = () => {
  if (!canAddCarouselCard.value) return;

  template.carouselCards.push(createCarouselCard());
};

const removeCarouselCard = index => {
  if (template.carouselCards.length <= 2) return;

  template.carouselCards.splice(index, 1);
  carouselMediaFileInputs.value.splice(index, 1);
};

const chooseTemplateMedia = () => {
  mediaFileInput.value?.click();
};

const clearTemplateMedia = () => {
  template.mediaSampleUrl = '';
  template.mediaSampleHandle = '';
  template.mediaSampleFile = null;
  template.mediaSampleFileName = '';
};

const handleTemplateMediaChange = event => {
  const [file] = event.target.files || [];
  if (!file) return;

  template.mediaSampleHandle = '';
  template.mediaSampleFile = file;
  template.mediaSampleFileName = file.name;
  template.mediaSampleUrl = URL.createObjectURL(file);
  event.target.value = '';
};

const setCarouselMediaInput = (element, index) => {
  if (element) carouselMediaFileInputs.value[index] = element;
};

const chooseCarouselMedia = index => {
  carouselMediaFileInputs.value[index]?.click();
};

const clearCarouselMedia = card => {
  card.mediaSampleUrl = '';
  card.mediaSampleHandle = '';
  card.mediaSampleFile = null;
  card.mediaSampleFileName = '';
};

const handleCarouselMediaChange = (event, card) => {
  const [file] = event.target.files || [];
  if (!file) return;

  card.mediaSampleHandle = '';
  card.mediaSampleFile = file;
  card.mediaSampleFileName = file.name;
  card.mediaSampleUrl = URL.createObjectURL(file);
  event.target.value = '';
};

const uploadMediaFile = async file => {
  const response = await store.dispatch('inboxes/uploadWhatsAppTemplateMedia', {
    inboxId: selectedInboxId.value,
    file,
  });

  if (!response.handle) {
    throw new Error(t('CAMPAIGN.TEMPLATES.BUILDER.API.MEDIA_UPLOAD_ERROR'));
  }

  return response.handle;
};

const uploadTemplateMedia = async () => {
  if (!hasHeaderMedia.value) return '';
  if (template.mediaSampleHandle) return template.mediaSampleHandle;

  isUploadingMedia.value = true;
  template.mediaSampleHandle = await uploadMediaFile(template.mediaSampleFile);
  return template.mediaSampleHandle;
};

const uploadCarouselMedia = async () => {
  isUploadingMedia.value = true;

  return Promise.all(
    template.carouselCards.map(async card => {
      if (!card.mediaSampleHandle) {
        card.mediaSampleHandle = await uploadMediaFile(card.mediaSampleFile);
      }
      return card.mediaSampleHandle;
    })
  );
};

const submitTemplate = async () => {
  showValidation.value = true;
  if (hasErrors.value) {
    useAlert(t('CAMPAIGN.TEMPLATES.BUILDER.API.VALIDATION_ERROR'));
    return;
  }

  if (isSubmitting.value) return;

  isSubmitting.value = true;

  try {
    const payload =
      template.format === 'CAROUSEL'
        ? buildCarouselPayload(await uploadCarouselMedia())
        : buildStandardPayload(await uploadTemplateMedia());

    await store.dispatch('inboxes/createWhatsAppTemplate', {
      inboxId: selectedInboxId.value,
      template: payload,
    });
    useAlert(t('CAMPAIGN.TEMPLATES.BUILDER.API.SUCCESS_MESSAGE'));
    router.push({ name: 'campaigns_templates_index' });
  } catch (error) {
    useAlert(error?.error || t('CAMPAIGN.TEMPLATES.BUILDER.API.ERROR_MESSAGE'));
  } finally {
    isSubmitting.value = false;
    isUploadingMedia.value = false;
  }
};

const goBack = () => {
  router.push({ name: 'campaigns_templates_index' });
};

watch(
  bodyVariables,
  variables => {
    variables.forEach(variable => {
      if (!(variable in template.bodySamples)) {
        template.bodySamples[variable] = '';
      }
    });

    Object.keys(template.bodySamples).forEach(variable => {
      if (!variables.includes(Number(variable))) {
        delete template.bodySamples[variable];
      }
    });
  },
  { immediate: true }
);

watch(
  () => template.format,
  value => {
    if (value === 'CAROUSEL') {
      template.category = 'MARKETING';
      template.headerFormat = 'NONE';
      template.headerText = '';
      template.headerSample = '';
      template.footer = '';
      template.buttons = [];
      clearTemplateMedia();
    }
  }
);

watch(
  () => template.carouselHeaderFormat,
  () => {
    template.carouselCards.forEach(card => clearCarouselMedia(card));
  }
);

watch(
  () => template.headerFormat,
  (value, oldValue) => {
    if (oldValue && value !== oldValue) {
      clearTemplateMedia();
    }

    if (value !== 'TEXT') {
      template.headerText = '';
      template.headerSample = '';
    }

    if (value === 'NONE') {
      clearTemplateMedia();
    }
  }
);

watch(
  () => template.category,
  () => {
    template.validityPeriod = validityPeriodOptions.value[0]?.value || '12h';
  }
);

watch(inboxOptions, options => {
  if (!selectedInboxId.value && options.length) {
    selectedInboxId.value = options[0].value;
  }
});

onMounted(() => {
  store.dispatch('inboxes/get');
});
</script>

<template>
  <div class="grid grid-rows-[auto_1fr] h-full w-full min-w-0 bg-n-surface-1">
    <SettingIntroBanner
      :header-title="t('CAMPAIGN.TEMPLATES.BUILDER.HEADER_TITLE')"
      :header-content="t('CAMPAIGN.TEMPLATES.BUILDER.HEADER_SUBTITLE')"
    >
      <div class="pb-3">
        <Button
          icon="i-lucide-arrow-left"
          :label="t('CAMPAIGN.TEMPLATES.BUILDER.ACTIONS.BACK')"
          variant="ghost"
          color="slate"
          size="sm"
          @click="goBack"
        />
      </div>
    </SettingIntroBanner>

    <section class="w-full overflow-auto py-6">
      <div
        class="mx-auto grid w-full max-w-7xl gap-6 px-6 xl:grid-cols-[1fr_24rem]"
      >
        <div class="min-w-0 space-y-4">
          <div class="rounded-lg border border-n-weak bg-n-surface-1 p-5">
            <div class="mb-5">
              <h3 class="mb-1 text-base font-medium text-n-slate-12">
                {{ t('CAMPAIGN.TEMPLATES.BUILDER.SECTIONS.SETUP') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{ t('CAMPAIGN.TEMPLATES.BUILDER.SECTIONS.SETUP_HINT') }}
              </p>
            </div>

            <div class="grid gap-4 md:grid-cols-2">
              <div class="min-w-0">
                <label class="mb-1 block text-sm font-medium text-n-slate-12">
                  {{ t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.INBOX') }}
                </label>
                <ComboBox
                  v-model="selectedInboxId"
                  :options="inboxOptions"
                  :class="comboBoxClass"
                  :message="showValidation ? errors.inbox : ''"
                  :has-error="showValidation && Boolean(errors.inbox)"
                />
              </div>

              <Input
                v-model="template.name"
                :label="t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.NAME')"
                :placeholder="t('CAMPAIGN.TEMPLATES.BUILDER.PLACEHOLDERS.NAME')"
                :message="showValidation ? errors.name : ''"
                :message-type="showValidation && errors.name ? 'error' : 'info'"
              />

              <div class="min-w-0 md:col-span-2">
                <label class="mb-1 block text-sm font-medium text-n-slate-12">
                  {{ t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.FORMAT') }}
                </label>
                <div class="grid gap-3 md:grid-cols-2">
                  <RadioCard
                    v-for="option in formatOptions"
                    :id="option.value"
                    :key="option.value"
                    :label="option.label"
                    :description="option.description"
                    :is-active="template.format === option.value"
                    @select="template.format = $event"
                  />
                </div>
              </div>

              <div class="min-w-0">
                <label class="mb-1 block text-sm font-medium text-n-slate-12">
                  {{ t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.LANGUAGE') }}
                </label>
                <ComboBox
                  v-model="template.language"
                  :options="languageOptions"
                  :class="comboBoxClass"
                  :message="showValidation ? errors.language : ''"
                  :has-error="showValidation && Boolean(errors.language)"
                />
              </div>

              <div class="min-w-0">
                <label class="mb-1 block text-sm font-medium text-n-slate-12">
                  {{ t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.CATEGORY') }}
                </label>
                <ComboBox
                  v-model="template.category"
                  :options="categoryOptions"
                  :class="comboBoxClass"
                  :message="showValidation ? errors.category : ''"
                  :has-error="showValidation && Boolean(errors.category)"
                />
              </div>
            </div>
          </div>

          <div class="rounded-lg border border-n-weak bg-n-surface-1 p-5">
            <div class="mb-5">
              <h3 class="mb-1 text-base font-medium text-n-slate-12">
                {{ t('CAMPAIGN.TEMPLATES.BUILDER.SECTIONS.COMPONENTS') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{ t('CAMPAIGN.TEMPLATES.BUILDER.SECTIONS.COMPONENTS_HINT') }}
              </p>
            </div>

            <div class="space-y-5">
              <div
                v-if="template.format === 'STANDARD'"
                class="rounded-lg border border-n-weak bg-n-alpha-2 p-4"
              >
                <div class="mb-3">
                  <h4 class="text-sm font-medium text-n-slate-12">
                    {{ t('CAMPAIGN.TEMPLATES.BUILDER.SECTIONS.HEADER') }}
                  </h4>
                </div>

                <div class="grid gap-4 md:grid-cols-2">
                  <div class="min-w-0">
                    <label
                      class="mb-1 block text-sm font-medium text-n-slate-12"
                    >
                      {{ t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.HEADER') }}
                    </label>
                    <ComboBox
                      v-model="template.headerFormat"
                      :options="headerFormatOptions"
                      :class="comboBoxClass"
                    />
                  </div>

                  <Input
                    v-if="template.headerFormat === 'TEXT'"
                    v-model="template.headerText"
                    :label="t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.HEADER_TEXT')"
                    :placeholder="
                      t('CAMPAIGN.TEMPLATES.BUILDER.PLACEHOLDERS.HEADER_TEXT')
                    "
                    :message="showValidation ? errors.headerText : ''"
                    :message-type="
                      showValidation && errors.headerText ? 'error' : 'info'
                    "
                  />

                  <Input
                    v-if="
                      template.headerFormat === 'TEXT' && headerVariables.length
                    "
                    v-model="template.headerSample"
                    :label="
                      t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.HEADER_SAMPLE')
                    "
                    :placeholder="
                      t('CAMPAIGN.TEMPLATES.BUILDER.PLACEHOLDERS.HEADER_SAMPLE')
                    "
                    :message="showValidation ? errors.headerSample : ''"
                    :message-type="
                      showValidation && errors.headerSample ? 'error' : 'info'
                    "
                  />

                  <div v-if="hasHeaderMedia" class="md:col-span-2">
                    <label
                      class="mb-1 block text-sm font-medium text-n-slate-12"
                    >
                      {{ t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.MEDIA_SAMPLE') }}
                    </label>
                    <input
                      ref="mediaFileInput"
                      :accept="mediaAccept"
                      type="file"
                      class="hidden"
                      @change="handleTemplateMediaChange"
                    />
                    <div class="flex flex-wrap items-center gap-2">
                      <Button
                        icon="i-lucide-upload"
                        :label="
                          template.mediaSampleFileName
                            ? t(
                                'CAMPAIGN.TEMPLATES.BUILDER.ACTIONS.CHANGE_MEDIA'
                              )
                            : t(
                                'CAMPAIGN.TEMPLATES.BUILDER.ACTIONS.CHOOSE_MEDIA'
                              )
                        "
                        size="sm"
                        :disabled="isSubmitting || !selectedInboxId"
                        @click="chooseTemplateMedia"
                      />
                      <Button
                        v-if="template.mediaSampleFileName"
                        icon="i-lucide-x"
                        :label="
                          t('CAMPAIGN.TEMPLATES.BUILDER.ACTIONS.REMOVE_MEDIA')
                        "
                        color="ruby"
                        variant="ghost"
                        size="sm"
                        :disabled="isSubmitting"
                        @click="clearTemplateMedia"
                      />
                    </div>
                    <p
                      v-if="template.mediaSampleFileName"
                      class="mt-1 text-xs text-n-slate-11"
                    >
                      {{ template.mediaSampleFileName }}
                    </p>
                    <p
                      v-if="isUploadingMedia"
                      class="mt-1 text-xs text-n-slate-11"
                    >
                      {{
                        t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.MEDIA_UPLOADING')
                      }}
                    </p>
                    <p
                      v-if="showValidation && errors.mediaSampleFile"
                      class="mt-1 text-xs text-n-ruby-9"
                    >
                      {{ errors.mediaSampleFile }}
                    </p>
                  </div>
                </div>
              </div>

              <div class="rounded-lg border border-n-weak bg-n-alpha-2 p-4">
                <div class="mb-3 flex items-start justify-between gap-4">
                  <div>
                    <h4 class="text-sm font-medium text-n-slate-12">
                      {{
                        template.format === 'CAROUSEL'
                          ? t(
                              'CAMPAIGN.TEMPLATES.BUILDER.SECTIONS.MESSAGE_BUBBLE'
                            )
                          : t('CAMPAIGN.TEMPLATES.BUILDER.SECTIONS.BODY')
                      }}
                    </h4>
                    <p class="mt-1 text-xs text-n-slate-11">
                      {{
                        template.format === 'CAROUSEL'
                          ? t('CAMPAIGN.TEMPLATES.BUILDER.HELP.MESSAGE_BUBBLE')
                          : t('CAMPAIGN.TEMPLATES.BUILDER.HELP.BODY_VARIABLES')
                      }}
                    </p>
                  </div>
                  <Button
                    v-if="template.format === 'STANDARD'"
                    icon="i-lucide-plus"
                    :label="
                      t('CAMPAIGN.TEMPLATES.BUILDER.ACTIONS.ADD_VARIABLE')
                    "
                    variant="faded"
                    color="slate"
                    size="sm"
                    @click="addBodyVariable"
                  />
                </div>

                <TextArea
                  v-model="template.body"
                  :label="
                    template.format === 'CAROUSEL'
                      ? t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.MESSAGE_BUBBLE')
                      : t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.BODY')
                  "
                  :placeholder="
                    template.format === 'CAROUSEL'
                      ? t(
                          'CAMPAIGN.TEMPLATES.BUILDER.PLACEHOLDERS.MESSAGE_BUBBLE'
                        )
                      : t('CAMPAIGN.TEMPLATES.BUILDER.PLACEHOLDERS.BODY')
                  "
                  :max-length="1024"
                  :message="showValidation ? errors.body : ''"
                  :message-type="
                    showValidation && errors.body ? 'error' : 'info'
                  "
                  show-character-count
                  auto-height
                  resize
                  min-height="8rem"
                />

                <div
                  v-if="template.format === 'STANDARD' && bodyVariables.length"
                  class="mt-4"
                >
                  <h5 class="mb-3 text-sm font-medium text-n-slate-12">
                    {{ t('CAMPAIGN.TEMPLATES.BUILDER.SECTIONS.SAMPLES') }}
                  </h5>
                  <div class="grid gap-3 md:grid-cols-2">
                    <Input
                      v-for="variable in bodyVariables"
                      :key="variable"
                      v-model="template.bodySamples[variable]"
                      :label="
                        t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.SAMPLE', {
                          variable,
                        })
                      "
                      :placeholder="
                        t('CAMPAIGN.TEMPLATES.BUILDER.PLACEHOLDERS.SAMPLE')
                      "
                      :message="
                        showValidation ? errors.bodySamples[variable] : ''
                      "
                      :message-type="
                        showValidation && errors.bodySamples[variable]
                          ? 'error'
                          : 'info'
                      "
                    />
                  </div>
                </div>
              </div>

              <div
                v-if="template.format === 'CAROUSEL'"
                class="rounded-lg border border-n-weak bg-n-alpha-2 p-4"
              >
                <div class="mb-4 flex items-start justify-between gap-4">
                  <div>
                    <h4 class="text-sm font-medium text-n-slate-12">
                      {{ t('CAMPAIGN.TEMPLATES.BUILDER.SECTIONS.CAROUSEL') }}
                    </h4>
                    <p class="mt-1 text-xs text-n-slate-11">
                      {{
                        t('CAMPAIGN.TEMPLATES.BUILDER.SECTIONS.CAROUSEL_HINT')
                      }}
                    </p>
                  </div>
                  <Button
                    icon="i-lucide-plus"
                    :label="t('CAMPAIGN.TEMPLATES.BUILDER.ACTIONS.ADD_CARD')"
                    variant="faded"
                    color="slate"
                    size="sm"
                    :disabled="!canAddCarouselCard"
                    @click="addCarouselCard"
                  />
                </div>

                <p
                  v-if="showValidation && errors.carousel"
                  class="mb-3 text-sm text-n-ruby-9"
                >
                  {{ errors.carousel }}
                </p>

                <div class="mb-4 max-w-64">
                  <label class="mb-1 block text-sm font-medium text-n-slate-12">
                    {{
                      t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.CAROUSEL_MEDIA_TYPE')
                    }}
                  </label>
                  <ComboBox
                    v-model="template.carouselHeaderFormat"
                    :options="carouselHeaderFormatOptions"
                    :class="comboBoxClass"
                  />
                </div>

                <div class="space-y-4">
                  <div
                    v-for="(card, cardIndex) in template.carouselCards"
                    :key="cardIndex"
                    class="rounded-lg border border-n-weak bg-n-surface-1 p-4"
                  >
                    <div class="mb-4 flex items-center justify-between gap-3">
                      <h5 class="text-sm font-medium text-n-slate-12">
                        {{
                          t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.CAROUSEL_CARD', {
                            index: cardIndex + 1,
                          })
                        }}
                      </h5>
                      <Button
                        icon="i-lucide-trash-2"
                        variant="ghost"
                        color="ruby"
                        size="sm"
                        :disabled="template.carouselCards.length <= 2"
                        @click="removeCarouselCard(cardIndex)"
                      />
                    </div>

                    <div class="mb-4">
                      <label
                        class="mb-1 block text-sm font-medium text-n-slate-12"
                      >
                        {{ t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.CARD_MEDIA') }}
                      </label>
                      <input
                        :ref="
                          element => setCarouselMediaInput(element, cardIndex)
                        "
                        :accept="carouselMediaAccept"
                        type="file"
                        class="hidden"
                        @change="
                          event => handleCarouselMediaChange(event, card)
                        "
                      />
                      <div class="flex flex-wrap items-center gap-2">
                        <Button
                          icon="i-lucide-upload"
                          :label="
                            card.mediaSampleFileName
                              ? t(
                                  'CAMPAIGN.TEMPLATES.BUILDER.ACTIONS.CHANGE_MEDIA'
                                )
                              : t(
                                  'CAMPAIGN.TEMPLATES.BUILDER.ACTIONS.CHOOSE_MEDIA'
                                )
                          "
                          size="sm"
                          :disabled="isSubmitting || !selectedInboxId"
                          @click="chooseCarouselMedia(cardIndex)"
                        />
                        <Button
                          v-if="card.mediaSampleFileName"
                          icon="i-lucide-x"
                          :label="
                            t('CAMPAIGN.TEMPLATES.BUILDER.ACTIONS.REMOVE_MEDIA')
                          "
                          color="ruby"
                          variant="ghost"
                          size="sm"
                          :disabled="isSubmitting"
                          @click="clearCarouselMedia(card)"
                        />
                      </div>
                      <p
                        v-if="card.mediaSampleFileName"
                        class="mt-1 text-xs text-n-slate-11"
                      >
                        {{ card.mediaSampleFileName }}
                      </p>
                      <p
                        v-if="
                          showValidation &&
                          errors.carouselCards[cardIndex]?.mediaSampleFile
                        "
                        class="mt-1 text-xs text-n-ruby-9"
                      >
                        {{ errors.carouselCards[cardIndex]?.mediaSampleFile }}
                      </p>
                    </div>

                    <TextArea
                      v-model="card.body"
                      :label="t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.CARD_BODY')"
                      :placeholder="
                        t('CAMPAIGN.TEMPLATES.BUILDER.PLACEHOLDERS.CARD_BODY')
                      "
                      :max-length="160"
                      :message="
                        showValidation
                          ? errors.carouselCards[cardIndex]?.body || ''
                          : ''
                      "
                      :message-type="
                        showValidation && errors.carouselCards[cardIndex]?.body
                          ? 'error'
                          : 'info'
                      "
                      show-character-count
                      auto-height
                      resize
                      min-height="5rem"
                    />

                    <div class="mt-4 space-y-3">
                      <div class="flex items-center justify-between gap-3">
                        <h6 class="text-sm font-medium text-n-slate-12">
                          {{ t('CAMPAIGN.TEMPLATES.BUILDER.SECTIONS.BUTTONS') }}
                        </h6>
                        <Button
                          icon="i-lucide-plus"
                          :label="
                            t('CAMPAIGN.TEMPLATES.BUILDER.ACTIONS.ADD_BUTTON')
                          "
                          variant="faded"
                          color="slate"
                          size="sm"
                          :disabled="card.buttons.length >= 2"
                          @click="card.buttons.push(createButton())"
                        />
                      </div>

                      <p
                        v-if="
                          showValidation &&
                          errors.carouselCards[cardIndex]?.buttons
                        "
                        class="text-sm text-n-ruby-9"
                      >
                        {{ errors.carouselCards[cardIndex]?.buttons }}
                      </p>

                      <div
                        v-for="(button, buttonIndex) in card.buttons"
                        :key="buttonIndex"
                        class="grid gap-3 rounded-lg border border-n-weak bg-n-alpha-2 p-3 md:grid-cols-[12rem_1fr_auto]"
                      >
                        <div>
                          <label
                            class="mb-1 block text-sm font-medium text-n-slate-12"
                          >
                            {{
                              t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.BUTTON_TYPE')
                            }}
                          </label>
                          <ComboBox
                            v-model="button.type"
                            :options="carouselButtonTypeOptions"
                            :class="comboBoxClass"
                          />
                        </div>

                        <Input
                          v-model="button.text"
                          :label="
                            t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.BUTTON_TEXT')
                          "
                          :placeholder="
                            t(
                              'CAMPAIGN.TEMPLATES.BUILDER.PLACEHOLDERS.BUTTON_TEXT'
                            )
                          "
                          :message="
                            showValidation
                              ? errors.carouselCards[cardIndex]?.buttonFields[
                                  buttonIndex
                                ]?.text || ''
                              : ''
                          "
                          :message-type="
                            showValidation &&
                            errors.carouselCards[cardIndex]?.buttonFields[
                              buttonIndex
                            ]?.text
                              ? 'error'
                              : 'info'
                          "
                        />

                        <Button
                          icon="i-lucide-trash-2"
                          variant="ghost"
                          color="ruby"
                          size="sm"
                          class="self-end"
                          :disabled="card.buttons.length <= 1"
                          @click="card.buttons.splice(buttonIndex, 1)"
                        />

                        <Input
                          v-if="button.type === 'URL'"
                          v-model="button.url"
                          class="md:col-span-2 md:col-start-2"
                          :label="
                            t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.BUTTON_URL')
                          "
                          :placeholder="
                            t(
                              'CAMPAIGN.TEMPLATES.BUILDER.PLACEHOLDERS.BUTTON_URL'
                            )
                          "
                          :message="
                            showValidation
                              ? errors.carouselCards[cardIndex]?.buttonFields[
                                  buttonIndex
                                ]?.url || ''
                              : ''
                          "
                          :message-type="
                            showValidation &&
                            errors.carouselCards[cardIndex]?.buttonFields[
                              buttonIndex
                            ]?.url
                              ? 'error'
                              : 'info'
                          "
                        />

                        <Input
                          v-if="button.type === 'PHONE_NUMBER'"
                          v-model="button.phoneNumber"
                          class="md:col-span-2 md:col-start-2"
                          :label="
                            t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.BUTTON_PHONE')
                          "
                          :placeholder="
                            t(
                              'CAMPAIGN.TEMPLATES.BUILDER.PLACEHOLDERS.BUTTON_PHONE'
                            )
                          "
                          :message="
                            showValidation
                              ? errors.carouselCards[cardIndex]?.buttonFields[
                                  buttonIndex
                                ]?.phoneNumber || ''
                              : ''
                          "
                          :message-type="
                            showValidation &&
                            errors.carouselCards[cardIndex]?.buttonFields[
                              buttonIndex
                            ]?.phoneNumber
                              ? 'error'
                              : 'info'
                          "
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="template.format === 'STANDARD'"
                class="rounded-lg border border-n-weak bg-n-alpha-2 p-4"
              >
                <div class="mb-3">
                  <h4 class="text-sm font-medium text-n-slate-12">
                    {{ t('CAMPAIGN.TEMPLATES.BUILDER.SECTIONS.FOOTER') }}
                  </h4>
                </div>

                <Input
                  v-model="template.footer"
                  :label="t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.FOOTER')"
                  :placeholder="
                    t('CAMPAIGN.TEMPLATES.BUILDER.PLACEHOLDERS.FOOTER')
                  "
                  :message="showValidation ? errors.footer : ''"
                  :message-type="
                    showValidation && errors.footer ? 'error' : 'info'
                  "
                />
              </div>
            </div>
          </div>

          <div
            v-if="template.format === 'STANDARD'"
            class="rounded-lg border border-n-weak bg-n-surface-1 p-5"
          >
            <div class="mb-5 flex items-start justify-between gap-4">
              <div>
                <h3 class="mb-1 text-base font-medium text-n-slate-12">
                  {{ t('CAMPAIGN.TEMPLATES.BUILDER.SECTIONS.BUTTONS') }}
                </h3>
                <p class="text-sm text-n-slate-11">
                  {{ t('CAMPAIGN.TEMPLATES.BUILDER.SECTIONS.BUTTONS_HINT') }}
                </p>
              </div>
              <Button
                icon="i-lucide-plus"
                :label="t('CAMPAIGN.TEMPLATES.BUILDER.ACTIONS.ADD_BUTTON')"
                variant="faded"
                color="slate"
                size="sm"
                :disabled="!canAddButton"
                @click="addButton"
              />
            </div>

            <p
              v-if="showValidation && errors.buttons"
              class="mb-3 text-sm text-n-ruby-9"
            >
              {{ errors.buttons }}
            </p>

            <div v-if="template.buttons.length" class="space-y-3">
              <div
                v-for="(button, index) in template.buttons"
                :key="index"
                class="grid gap-3 rounded-lg border border-n-weak bg-n-alpha-2 p-3 md:grid-cols-[12rem_1fr_auto]"
              >
                <div>
                  <label class="mb-1 block text-sm font-medium text-n-slate-12">
                    {{ t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.BUTTON_TYPE') }}
                  </label>
                  <ComboBox
                    v-model="button.type"
                    :options="buttonTypeOptions"
                    :class="comboBoxClass"
                  />
                </div>

                <Input
                  v-if="button.type !== 'COPY_CODE'"
                  v-model="button.text"
                  :label="t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.BUTTON_TEXT')"
                  :placeholder="
                    t('CAMPAIGN.TEMPLATES.BUILDER.PLACEHOLDERS.BUTTON_TEXT')
                  "
                  :message="
                    showValidation ? errors.buttonFields[index]?.text || '' : ''
                  "
                  :message-type="
                    showValidation && errors.buttonFields[index]?.text
                      ? 'error'
                      : 'info'
                  "
                />

                <Input
                  v-else
                  v-model="button.example"
                  :label="t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.COPY_CODE')"
                  :placeholder="
                    t('CAMPAIGN.TEMPLATES.BUILDER.PLACEHOLDERS.COPY_CODE')
                  "
                  :message="
                    showValidation
                      ? errors.buttonFields[index]?.example || ''
                      : ''
                  "
                  :message-type="
                    showValidation && errors.buttonFields[index]?.example
                      ? 'error'
                      : 'info'
                  "
                />

                <Button
                  icon="i-lucide-trash-2"
                  variant="ghost"
                  color="ruby"
                  size="sm"
                  class="self-end"
                  @click="removeButton(index)"
                />

                <Input
                  v-if="button.type === 'URL'"
                  v-model="button.url"
                  class="md:col-span-2 md:col-start-2"
                  :label="t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.BUTTON_URL')"
                  :placeholder="
                    t('CAMPAIGN.TEMPLATES.BUILDER.PLACEHOLDERS.BUTTON_URL')
                  "
                  :message="
                    showValidation ? errors.buttonFields[index]?.url || '' : ''
                  "
                  :message-type="
                    showValidation && errors.buttonFields[index]?.url
                      ? 'error'
                      : 'info'
                  "
                />

                <Input
                  v-if="button.type === 'PHONE_NUMBER'"
                  v-model="button.phoneNumber"
                  class="md:col-span-2 md:col-start-2"
                  :label="t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.BUTTON_PHONE')"
                  :placeholder="
                    t('CAMPAIGN.TEMPLATES.BUILDER.PLACEHOLDERS.BUTTON_PHONE')
                  "
                  :message="
                    showValidation
                      ? errors.buttonFields[index]?.phoneNumber || ''
                      : ''
                  "
                  :message-type="
                    showValidation && errors.buttonFields[index]?.phoneNumber
                      ? 'error'
                      : 'info'
                  "
                />
              </div>
            </div>

            <div
              v-else
              class="rounded-lg border border-dashed border-n-weak p-4 text-sm text-n-slate-11"
            >
              {{ t('CAMPAIGN.TEMPLATES.BUILDER.BUTTONS.EMPTY') }}
            </div>
          </div>

          <div class="rounded-lg border border-n-weak bg-n-surface-1 p-5">
            <div
              class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between"
            >
              <div>
                <h3 class="mb-1 text-base font-medium text-n-slate-12">
                  {{ t('CAMPAIGN.TEMPLATES.BUILDER.SECTIONS.VALIDITY') }}
                </h3>
                <p class="text-sm text-n-slate-11">
                  {{ t('CAMPAIGN.TEMPLATES.BUILDER.SECTIONS.VALIDITY_HINT') }}
                </p>
              </div>

              <label class="flex items-center gap-2 text-sm text-n-slate-12">
                <input
                  v-model="template.hasCustomValidityPeriod"
                  type="checkbox"
                  class="m-0"
                />
                <span>
                  {{ t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.CUSTOM_VALIDITY') }}
                </span>
              </label>
            </div>

            <div v-if="template.hasCustomValidityPeriod" class="mt-4 max-w-56">
              <label class="mb-1 block text-sm font-medium text-n-slate-12">
                {{ t('CAMPAIGN.TEMPLATES.BUILDER.FIELDS.VALIDITY_PERIOD') }}
              </label>
              <ComboBox
                v-model="template.validityPeriod"
                :options="validityPeriodOptions"
                :class="comboBoxClass"
              />
            </div>
          </div>

          <div
            class="flex items-center justify-end gap-3 rounded-lg border border-n-weak bg-n-surface-1 p-4"
          >
            <Button
              :label="t('CAMPAIGN.TEMPLATES.BUILDER.ACTIONS.CANCEL')"
              variant="ghost"
              color="slate"
              @click="goBack"
            />
            <Button
              icon="i-lucide-send"
              :label="t('CAMPAIGN.TEMPLATES.BUILDER.ACTIONS.SUBMIT_DRAFT')"
              :disabled="isSubmitting || isUploadingMedia"
              :is-loading="isSubmitting || isUploadingMedia"
              @click="submitTemplate"
            />
          </div>
        </div>

        <aside class="min-w-0 xl:sticky xl:top-6 xl:self-start">
          <div class="rounded-lg border border-n-weak bg-n-surface-1 p-4">
            <div class="mb-4">
              <h3 class="text-base font-medium text-n-slate-12">
                {{ t('CAMPAIGN.TEMPLATES.BUILDER.PREVIEW.TITLE') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{ selectedInboxName }}
              </p>
            </div>

            <div class="rounded-[2rem] border border-n-weak bg-n-slate-3 p-3">
              <div class="rounded-[1.5rem] bg-n-surface-1 p-3">
                <div
                  class="mb-3 flex items-center gap-2 border-b border-n-weak pb-3"
                >
                  <div
                    class="flex size-9 items-center justify-center rounded-full bg-n-teal-9 text-white"
                  >
                    <Icon icon="i-lucide-message-circle" class="size-5" />
                  </div>
                  <div class="min-w-0">
                    <p class="truncate text-sm font-medium text-n-slate-12">
                      {{ t('CAMPAIGN.TEMPLATES.BUILDER.PREVIEW.WHATSAPP') }}
                    </p>
                    <p class="truncate text-xs text-n-slate-10">
                      {{
                        template.name ||
                        t('CAMPAIGN.TEMPLATES.BUILDER.PREVIEW.NAME_PLACEHOLDER')
                      }}
                    </p>
                  </div>
                </div>

                <div
                  v-if="template.format === 'STANDARD'"
                  class="max-w-[18rem] rounded-lg bg-n-teal-3 p-3 text-sm text-n-slate-12"
                >
                  <div
                    v-if="template.headerFormat === 'TEXT' && headerWithSample"
                    class="mb-2 font-medium"
                  >
                    {{ headerWithSample }}
                  </div>

                  <div
                    v-if="hasHeaderMedia"
                    class="mb-2 flex h-28 items-center justify-center rounded-lg bg-n-slate-4 text-n-slate-11"
                  >
                    <img
                      v-if="
                        template.headerFormat === 'IMAGE' &&
                        template.mediaSampleUrl
                      "
                      :src="template.mediaSampleUrl"
                      :alt="headerFormatLabel"
                      class="size-full rounded-lg object-cover"
                    />
                    <div v-else class="text-center">
                      <Icon icon="i-lucide-file" class="mx-auto mb-1 size-6" />
                      <span class="text-xs">{{ headerFormatLabel }}</span>
                    </div>
                  </div>

                  <p class="whitespace-pre-wrap leading-5">
                    {{
                      bodyWithSamples ||
                      t('CAMPAIGN.TEMPLATES.BUILDER.PREVIEW.BODY_PLACEHOLDER')
                    }}
                  </p>

                  <p
                    v-if="template.footer"
                    class="mt-2 border-t border-n-weak pt-2 text-xs text-n-slate-10"
                  >
                    {{ template.footer }}
                  </p>

                  <div v-if="visibleButtons.length" class="mt-3 space-y-2">
                    <button
                      v-for="(button, index) in visibleButtons"
                      :key="index"
                      type="button"
                      class="h-8 w-full rounded-md bg-n-surface-1 px-3 text-sm font-medium text-n-blue-11"
                    >
                      {{
                        button.type === 'COPY_CODE'
                          ? button.example
                          : button.text
                      }}
                    </button>
                  </div>
                </div>

                <div v-else class="space-y-3 text-sm text-n-slate-12">
                  <div class="max-w-[18rem] rounded-lg bg-n-teal-3 p-3">
                    <p class="whitespace-pre-wrap leading-5">
                      {{ bodyWithSamples || previewBodyPlaceholder }}
                    </p>
                  </div>

                  <div class="flex gap-3 overflow-x-auto pb-1">
                    <div
                      v-for="(card, cardIndex) in template.carouselCards"
                      :key="cardIndex"
                      class="w-48 shrink-0 rounded-lg bg-n-teal-3 p-3"
                    >
                      <div
                        class="mb-2 flex h-24 items-center justify-center rounded-lg bg-n-slate-4 text-n-slate-11"
                      >
                        <img
                          v-if="
                            template.carouselHeaderFormat === 'IMAGE' &&
                            card.mediaSampleUrl
                          "
                          :src="card.mediaSampleUrl"
                          :alt="carouselHeaderFormatLabel"
                          class="size-full rounded-lg object-cover"
                        />
                        <div v-else class="text-center">
                          <Icon
                            icon="i-lucide-file"
                            class="mx-auto mb-1 size-6"
                          />
                          <span class="text-xs">
                            {{ carouselHeaderFormatLabel }}
                          </span>
                        </div>
                      </div>
                      <p class="min-h-12 whitespace-pre-wrap leading-5">
                        {{
                          card.body ||
                          t(
                            'CAMPAIGN.TEMPLATES.BUILDER.PREVIEW.CARD_PLACEHOLDER'
                          )
                        }}
                      </p>
                      <div class="mt-3 space-y-2">
                        <button
                          v-for="(button, buttonIndex) in card.buttons.filter(
                            item => item.text.trim()
                          )"
                          :key="buttonIndex"
                          type="button"
                          class="h-8 w-full rounded-md bg-n-surface-1 px-3 text-sm font-medium text-n-blue-11"
                        >
                          {{ button.text }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="allVariables.length"
              class="mt-4 rounded-lg bg-n-alpha-2 p-3 text-xs text-n-slate-11"
            >
              <p class="mb-2 font-medium text-n-slate-12">
                {{ t('CAMPAIGN.TEMPLATES.BUILDER.PREVIEW.SAMPLES') }}
              </p>
              <div class="space-y-1">
                <p v-if="headerVariables.length">
                  {{ variableLabelWithColon(1) }}
                  {{
                    template.headerSample ||
                    t('CAMPAIGN.TEMPLATES.BUILDER.PREVIEW.SAMPLE_EMPTY')
                  }}
                </p>
                <p v-for="variable in bodyVariables" :key="variable">
                  {{ variableLabelWithColon(variable) }}
                  {{
                    template.bodySamples[variable] ||
                    t('CAMPAIGN.TEMPLATES.BUILDER.PREVIEW.SAMPLE_EMPTY')
                  }}
                </p>
              </div>
            </div>

            <div
              class="mt-4 rounded-lg bg-n-alpha-2 p-3 text-xs text-n-slate-11"
            >
              {{ t('CAMPAIGN.TEMPLATES.BUILDER.PREVIEW.NOTE') }}
            </div>
          </div>
        </aside>
      </div>
    </section>
  </div>
</template>
